import { Transition } from "@headlessui/react";
import {
  ApplicantAnotherExperience,
  ApplicantArticle,
  ApplicantAward,
  ApplicantCommunity,
  ApplicantContest,
  ApplicantEducation,
  ApplicantMentorship,
  ApplicantOpenSource,
  ApplicantPatent,
  ApplicantPublicTalk,
  ApplicantRefereeFeature,
  ApplicantRefereeRelationshipTag,
  ApplicantRefereeSignatureLikelihoodTag,
  User,
} from "@prisma/client";
import { ApplicationSection } from "@prisma/client";
import animateScrollTo from "animated-scroll-to";
import Badge, { BadgeType } from "immigram-ui-cmon/src/components/Badge/Badge";
import Button, {
  ButtonType,
} from "immigram-ui-cmon/src/components/Button/Button";
import { CommentsContext } from "immigram-ui-cmon/src/components/Comments/context";
import Heading, {
  HeadingTag,
} from "immigram-ui-cmon/src/components/Heading/Heading";
import {
  Icon20DocDownloaded,
  Icon24Cancel,
  Icon24ChevronDown,
  Icon24ChevronUp,
} from "immigram-ui-cmon/src/components/Icons";
import { showNotification } from "immigram-ui-cmon/src/components/Notification/Notification";
import Paragraph from "immigram-ui-cmon/src/components/Paragraph/Paragraph";
import { TextAreaMinRows } from "immigram-ui-cmon/src/components/TextArea/TextArea";
import clsxm from "immigram-ui-cmon/src/lib/clsxm";
import { Trans, useTranslation } from "next-i18next";
import { TFunction } from "next-i18next";
import {
  Fragment,
  MouseEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Control } from "react-hook-form";

import { getApplicationSection } from "@/lib/application";
import {
  ApplicationSectionColumn,
  ApplicationSectionStatusType,
  getApplicationSectionStatusFromArrayItem,
} from "@/lib/applicationSectionStatus";
import { isAdmin } from "@/lib/auth/rbac";
import { useIsFormDisabled } from "@/lib/domainHook/useIsFormDisabled";
import { ApplicationSectionName } from "@/lib/experienceType";
import {
  DocType,
  useApplicationDocList,
  useGenerateDoc,
} from "@/lib/google-doc";
import useEffectOnce from "@/lib/hook/useEffectOnce";
import { useOnQueryShowErrors } from "@/lib/hook/useOnQueryShowErrors";
import { ValidationError } from "@/lib/hook/useValidation";
import {
  MondayPlatformStatus,
  prepareMondayPlatformStatus,
} from "@/lib/integration/monday/lib/status";
import {
  ApplicantMilestoneFull,
  ApplicantProjectFull,
  ApplicantRefereeFull,
  ApplicantRefereeList,
  ApplicationFormPrepared,
  ApplicationFull,
} from "@/lib/prisma";
import { Event, useTracking } from "@/lib/tracking";

import { showConfirmModal } from "@/components/Modal/ModalController";

import { saveApplicationSection } from "@/core/client/application/applicationSection";
import { updateMondayFetItemPlatformStatus } from "@/core/client/monday/platformStatus";
import { updateMondayFetItemReferee } from "@/core/client/monday/referee";
import { createRefLetterFromDetails } from "@/core/client/referee/details/refLetter";
import { useRollbackMutation } from "@/core/client/referee/details/rollback";

import {
  getRefereeAssignedExperience,
  RefereeGroupName,
  RefereeType,
} from "./common";
import ControlledTextAreaWithInlineHelp from "../FormElements/ControlledTextAreaWithInlineHelp";
import ConfirmModal from "../Modal/ConfirmModal";
import ValidationModal from "../Modal/ValidationModal";

interface RefereeDetailsProps {
  refereeType: RefereeType;
  control: Control<ApplicantRefereeList>;
  index: number;
  field: ApplicantRefereeFull;
  groupName: RefereeGroupName;
  ownUser: User;
  application: ApplicationFull;
  applicationSectionStatus: ApplicationSectionStatusType | null;
  applicantRefereeList: ApplicantRefereeList;
  refereeRelashionshipTags: ApplicantRefereeRelationshipTag[];
  signatureLikelihoodTags: ApplicantRefereeSignatureLikelihoodTag[];
  applicantRefereeFeatures: ApplicantRefereeFeature[];
  applicantProjectList?: ApplicantProjectFull[];
  applicantMilestoneList?: ApplicantMilestoneFull[];
  applicantPublicTalkList?: ApplicantPublicTalk[];
  applicantContestList?: ApplicantContest[];
  applicantOpenSourceList?: ApplicantOpenSource[];
  applicantMentorshipList?: ApplicantMentorship[];
  applicantTeachingList?: ApplicantEducation[];
  applicantCommunityList?: ApplicantCommunity[];
  applicantAnotherExperienceList?: ApplicantAnotherExperience[];
  applicantArticleList?: ApplicantArticle[];
  applicantAwardList?: ApplicantAward[];
  applicantPatentList?: ApplicantPatent[];
  applicationProfJourneyFormList?: ApplicationFormPrepared[];
  isLastRefereeToSubmit: boolean;
  errors?: Array<ValidationError | null>;
  onChange: (fieldName: string, value: any) => void;
  onDelete: () => void;
  onStatusChange?: (
    refereeId: string,
    status: ApplicationSectionStatusType | null
  ) => void;
}

export const getPersonDescriptionText = (
  groupName: RefereeGroupName,
  t: TFunction,
  options?: { isO1Visa?: boolean }
) => ({
  heading: t(
    "application:referees.refereeDetails.getPersonDescriptionText.heading"
  ),
  description: t(
    options?.isO1Visa
      ? "application:referees.refereeDetails.o1.getPersonDescriptionText.description"
      : "application:referees.refereeDetails.getPersonDescriptionText.description"
  ),
  example:
    groupName === RefereeGroupName.WorkedWith ? (
      <>
        <Paragraph className="my-0 text-sm text-black-600">
          {t(
            "application:referees.refereeDetails.getPersonDescriptionText.example.workedWith.paragraph1"
          )}
        </Paragraph>
        <Paragraph className="my-0 text-sm text-black-600">
          {t(
            "application:referees.refereeDetails.getPersonDescriptionText.example.workedWith.paragraph2"
          )}
        </Paragraph>
      </>
    ) : groupName === RefereeGroupName.DidntWorkWith ? (
      <>
        <Paragraph className="my-0 text-sm text-black-600">
          {t(
            "application:referees.refereeDetails.getPersonDescriptionText.example.didntWorkWith.paragraph1"
          )}
        </Paragraph>
        <Paragraph className="my-0 text-sm text-black-600">
          {t(
            "application:referees.refereeDetails.getPersonDescriptionText.example.didntWorkWith.paragraph2"
          )}
        </Paragraph>
      </>
    ) : null,
});

export const getAssessmentDescriptionText = (
  groupName: RefereeGroupName,
  t: TFunction
) => ({
  heading: t(
    "application:referees.refereeDetails.getAssessmentDescriptionText.heading"
  ),
  description: t(
    "application:referees.refereeDetails.getAssessmentDescriptionText.description"
  ),
  example:
    groupName === RefereeGroupName.WorkedWith ? (
      <>
        <Paragraph className="my-0 text-sm text-black-600">
          {t(
            "application:referees.refereeDetails.getAssessmentDescriptionText.example.workedWith.paragraph1"
          )}
        </Paragraph>
        <Paragraph className="my-0 text-sm text-black-600">
          {t(
            "application:referees.refereeDetails.getAssessmentDescriptionText.example.workedWith.paragraph2"
          )}
        </Paragraph>
      </>
    ) : groupName === RefereeGroupName.DidntWorkWith ? (
      <>
        <Paragraph className="my-0 text-sm text-black-600">
          {t(
            "application:referees.refereeDetails.getAssessmentDescriptionText.example.didntWorkWith.paragraph1"
          )}
        </Paragraph>
      </>
    ) : null,
});

export const getMeetDescriptionText = (
  groupName: RefereeGroupName,
  t: TFunction
) => ({
  heading: t(
    "application:referees.refereeDetails.getMeetDescriptionText.heading"
  ),
  description:
    groupName === RefereeGroupName.WorkedWith
      ? t(
          "application:referees.refereeDetails.getMeetDescriptionText.description.workedWith"
        )
      : groupName === RefereeGroupName.DidntWorkWith
      ? t(
          "application:referees.refereeDetails.getMeetDescriptionText.description.didntWorkWith"
        )
      : null,
  example:
    groupName === RefereeGroupName.WorkedWith ? (
      <>
        <Paragraph className="my-0 text-sm text-black-600">
          {t(
            "application:referees.refereeDetails.getMeetDescriptionText.example.workedWith.paragraph1"
          )}
        </Paragraph>
      </>
    ) : groupName === RefereeGroupName.DidntWorkWith ? (
      <>
        <Paragraph className="my-0 text-sm text-black-600">
          {t(
            "application:referees.refereeDetails.getMeetDescriptionText.example.didntWorkWith.paragraph1"
          )}
        </Paragraph>
        <Paragraph className="my-0 text-sm text-black-600">
          {t(
            "application:referees.refereeDetails.getMeetDescriptionText.example.didntWorkWith.paragraph2"
          )}
        </Paragraph>
      </>
    ) : null,
});

export const getCommonWorksDescriptionText = (
  groupName: RefereeGroupName,
  t: TFunction,
  options?: { isO1Visa?: boolean }
) => ({
  heading:
    groupName === RefereeGroupName.WorkedWith
      ? t(
          options?.isO1Visa
            ? "application:o1.referees.refereeDetails.getCommonWorksDescriptionText.heading.workedWith"
            : "application:referees.refereeDetails.getCommonWorksDescriptionText.heading.workedWith"
        )
      : groupName === RefereeGroupName.DidntWorkWith
      ? t(
          "application:referees.refereeDetails.getCommonWorksDescriptionText.heading.didntWorkWith"
        )
      : null,
  description:
    groupName === RefereeGroupName.WorkedWith
      ? t(
          options?.isO1Visa
            ? "application:o1.referees.refereeDetails.getCommonWorksDescriptionText.description.workedWith"
            : "application:referees.refereeDetails.getCommonWorksDescriptionText.description.workedWith"
        )
      : groupName === RefereeGroupName.DidntWorkWith
      ? t(
          "application:referees.refereeDetails.getCommonWorksDescriptionText.description.didntWorkWith"
        )
      : null,
  example:
    groupName === RefereeGroupName.WorkedWith ? (
      <>
        <Paragraph className="my-0 text-sm text-black-600">
          {t(
            "application:referees.refereeDetails.getCommonWorksDescriptionText.example.workedWith.paragraph1"
          )}
        </Paragraph>
      </>
    ) : groupName === RefereeGroupName.DidntWorkWith ? (
      <>
        <Paragraph className="my-0 text-sm text-black-600">
          {t(
            "application:referees.refereeDetails.getCommonWorksDescriptionText.example.workedWith.paragraph2"
          )}
        </Paragraph>
      </>
    ) : null,
});

export const getPossibleWorksDescriptionText = (t: TFunction) => ({
  heading: t(
    "application:referees.refereeDetails.getPossibleWorksDescriptionText.heading"
  ),
  description: t(
    "application:referees.refereeDetails.getPossibleWorksDescriptionText.description"
  ),
  example: (
    <>
      <Paragraph className="my-0 text-sm text-black-600">
        {t(
          "application:referees.refereeDetails.getPossibleWorksDescriptionText.example.paragraph"
        )}
      </Paragraph>
    </>
  ),
});

export const getLevelOfExcitementText = (t: TFunction) => ({
  heading: t(
    "application:referees.refereeDetails.getLevelOfExcitementText.heading"
  ),
  description: t(
    "application:referees.refereeDetails.getLevelOfExcitementText.description"
  ),
  example: null,
});

export const getContactDetails = (t: TFunction) => ({
  heading: t("application:referees.refereeDetails.contactDetails.heading"),
  description: t(
    "application:referees.refereeDetails.contactDetails.description"
  ),
  example: null,
});

const RefereeDetails = (props: RefereeDetailsProps) => {
  const { t } = useTranslation();

  const { trackEvent } = useTracking();

  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (!props.errors) {
      setShowErrors(false);
    }
  }, [props.errors]);

  useOnQueryShowErrors(() => setShowErrors(true));

  const fiedsErrors = showErrors
    ? props.errors?.reduce<Record<string, string>>(
        (acc, err) => ({
          ...acc,
          ...(err && {
            [err.fieldName]: acc[err.fieldName] || err.message,
          }),
        }),
        {}
      )
    : null;

  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] =
    useState(false);
  const [isSubmitConfirmModalOpen, setIsSubmitConfirmModalOpen] =
    useState(false);
  const [isSubmitSucceedModalOpen, setIsSubmitSucceedModalOpen] =
    useState(false);
  const [isSectionFinishedModalOpen, setIsSectionFinishedModalOpen] =
    useState(false);

  const [openedReferees, setOpenedReferees] = useState<Array<string>>([]);
  const ctx = useContext(CommentsContext);

  const handleCollapse = (id: string) => {
    ctx.setShowedThreadName && ctx.setShowedThreadName("");
    if (openedReferees.includes(id)) {
      setOpenedReferees((x) => x.filter((y) => y !== id));
    } else {
      setOpenedReferees((x) => [...x, id]);
    }
  };
  useEffectOnce(() => {
    if (props.field) {
      setOpenedReferees([props.field.id]);
    }
  });

  const [applicationSectionStatusLocal, setApplicationSectionStatusLocal] =
    useState(
      getApplicationSectionStatusFromArrayItem(
        props.application,
        ApplicationSectionColumn.RefereeDetails,
        props.field.id
      )
    );

  // Получаем завершённое письмо рефери (completionStatus = 'finish')
  const { data: applicationDocs } = useApplicationDocList(props.application.id);
  const finishedRefereeLetter = useMemo(() => {
    if (!applicationDocs) return null;
    return (
      applicationDocs.find((doc) => {
        const meta = (doc.meta || {}) as Record<string, any>;
        const refereeIdFromMeta =
          (meta.refereeId as string | undefined) ||
          // поддержка старых записей с опечаткой в ключе
          (meta.refereeld as string | undefined);
        return (
          doc.type === DocType.EmptyRefereeLetterDoc &&
          refereeIdFromMeta === props.field.id &&
          doc.completionStatus === "finish"
        );
      }) || null
    );
  }, [applicationDocs, props.field.id]);
  const { field } = props;
  useEffect(() => {
    if (
      applicationSectionStatusLocal ===
      ApplicationSectionStatusType.ReviewCompleted
    ) {
      setOpenedReferees((x) => x.filter((y) => y !== field.id));
    }
  }, [applicationSectionStatusLocal, field]);

  const { isFormDisabled } = useIsFormDisabled(
    props.application,
    applicationSectionStatusLocal
  );

  const { mutateAsync: rollback } = useRollbackMutation();

  const refereeNameForDocs = `${props.field.firstName ?? ""}${
    props.field.lastName ? ` ${props.field.lastName}` : ""
  }`;

  const isO1Visa = props.application?.visaType?.slug === "o1";

  const generateRefereeDoc = useGenerateDoc<DocType.RefereeDoc>({
    type: DocType.RefereeDoc,
    name: `Referee ${refereeNameForDocs}`,
    googleDriveDir: props.application.googleDrivePlatformDocsDir,
    data: {
      ...props,
      applicantReferee: props.field,
    },
    meta: {
      refereeId: props.field.id,
      refereeName: refereeNameForDocs,
    },
  } as any);

  const handleGenerateDoc = useCallback(
    () =>
      showConfirmModal({
        heading: t("application:referee.details.generateDocModal.heading"),
        text: t("application:referee.details.generateDocModal.text"),
        onConfirm: async () => {
          const refereeDocResult = await generateRefereeDoc();
          if (refereeDocResult) {
            await createRefLetterFromDetails({
              applicationId: props.application.id,
              refereeId: props.field.id,
              refereeName: refereeNameForDocs,
              generationOptions: {
                refLetterNumber: props.field.refLetterNumber,
                refLetterTargetCriteria: props.field.refLetterTargetCriteria,
                refLetterEnglishVariant: props.field.refLetterEnglishVariant,
                refLetterTonePreference: props.field.refLetterTonePreference,
              },
            });

            showNotification({
              variant: "success",
              title: t(
                "application:referee.details.generateDoc.success.notification"
              ),
            });
          } else {
            showNotification({
              variant: "alert",
              title: t(
                "application:referee.details.generateDoc.error.notification"
              ),
            });
          }
        },
      }),
    [
      generateRefereeDoc,
      t,
      props.application.id,
      props.field.id,
      props.field.refLetterNumber,
      props.field.refLetterTargetCriteria,
      props.field.refLetterEnglishVariant,
      props.field.refLetterTonePreference,
      refereeNameForDocs,
    ]
  );

  const handleSectionRollback = useCallback(async () => {
    showConfirmModal({
      heading: t(
        "application:profJourney.new.potential.sectionRollbackModal.heading"
      ),
      text: t(
        "application:profJourney.new.potential.sectionRollbackModal.text"
      ),
      confirmButtonText: t(
        "application:profJourney.new.potential.sectionRollbackModal.confirmButtonText"
      ),
      cancelButtonText: t(
        "application:profJourney.new.potential.sectionRollbackModal.cancelButtonText"
      ),
      onConfirm: async () => {
        try {
          rollback({
            applicationId: props.application.id,
            refereeId: props.field.id,
          });
          setApplicationSectionStatusLocal(
            ApplicationSectionStatusType.Created
          );
          showNotification({
            variant: "success",
            title: `${t(
              "application:referee.details.sectionRollback.success"
            )}`,
          });
          trackEvent(Event.RollbackRefereeDetails);
        } catch (e) {
          showNotification({
            variant: "alert",
            title: `${t(
              "application:profJourney.new.potential.companyDelete.title.failedToRollbackSection"
            )}`,
          });
        }
      },
    });
  }, [props.application, props.field.id, rollback, t, trackEvent]);

  const handleConfirmModalYesClick = async () => {
    const refereeName = refereeNameForDocs;

    setApplicationSectionStatusLocal(
      ApplicationSectionStatusType.ReviewCompleted
    );

    const assignedExperienceOptions = getRefereeAssignedExperience(props, t);

    const { projectNames, employmentNames } = assignedExperienceOptions.reduce(
      (acc, el) => {
        if (props.field.experienceListAssigned?.includes(el.value)) {
          if (el.group === RefereeGroupName.WorkedWith) {
            acc.employmentNames.push(el.title as string);
          } else if (el.group === RefereeGroupName.DidntWorkWith) {
            acc.projectNames.push(el.title as string);
          }
        }

        return acc;
      },
      {
        projectNames: [],
        employmentNames: [],
      } as {
        projectNames: string[];
        employmentNames: string[];
      }
    );

    const additionalData: Record<string, any> = { refereeName };

    if (projectNames.length) {
      additionalData.projectNames = projectNames;
    }
    if (employmentNames.length) {
      additionalData.employmentNames = employmentNames;
    }

    await updateMondayFetItemPlatformStatus(
      props.application.applicant.user.mondayClientItemId,
      prepareMondayPlatformStatus(
        ApplicationSectionName.RefereeDetails,
        MondayPlatformStatus.COMPLETE_DETAILED_REF
      ),
      additionalData
    );

    const section = getApplicationSection(props.application);

    const resultSection = {
      id: section.id,
      applicationId: props.application.id,
      [ApplicationSectionColumn.RefereeDetails]: {
        id: props.field.id,
        statusId: ApplicationSectionStatusType.ReviewCompleted,
      },
    } as any as ApplicationSection;

    await saveApplicationSection(props.application.id, resultSection);

    if (props.onStatusChange) {
      props.onStatusChange(
        props.field.id,
        ApplicationSectionStatusType.ReviewCompleted
      );
    }

    animateScrollTo(0);

    trackEvent(Event.SubmitForReviewRefereeDetails, {
      refereeName,
    });

    updateMondayFetItemReferee(
      props.application.applicant.user.mondayClientItemId,
      refereeNameForDocs
    );

    const refereeDocResult = await generateRefereeDoc();

    if (!refereeDocResult) {
      console.error("Failed to generate referee doc");
      showNotification({
        variant: "alert",
        title: t("application:referee.details.generateDoc.error.notification"),
      });
      return;
    }

    console.log(
      "✅ Referee doc created:",
      refereeDocResult.id,
      refereeDocResult.fileName
    );

    await new Promise((resolve) => setTimeout(resolve, 500));

    const refLetterResult = await createRefLetterFromDetails({
      applicationId: props.application.id,
      refereeId: props.field.id,
      refereeName: refereeNameForDocs,
      generationOptions: {
        refLetterNumber: props.field.refLetterNumber,
        refLetterTargetCriteria: props.field.refLetterTargetCriteria,
        refLetterEnglishVariant: props.field.refLetterEnglishVariant,
        refLetterTonePreference: props.field.refLetterTonePreference,
      },
    });

    if (!refLetterResult) {
      console.error("Failed to create ref letter from details");
      showNotification({
        variant: "alert",
        title: t("application:referee.details.generateDoc.error.notification"),
      });
    } else {
      console.log(
        "✅ Ref letter created:",
        refLetterResult.id,
        refLetterResult.fileName
      );
    }
  };

  return (
    <>
      <div key={props.field.id} className="group">
        <div className="-ml-4 -mr-4 border-t border-stone-200 group-first:hidden" />

        <div
          className={clsxm(
            "group flex w-full cursor-pointer items-center py-4"
          )}
          onClick={() => handleCollapse(props.field.id)}
        >
          <Heading
            className="my-0 transition group-hover:text-black-600"
            tag={HeadingTag.H6}
          >
            <span className="inline-block">
              {props.field.firstName} {props.field.lastName}
            </span>
          </Heading>
          <div className="ml-auto flex flex-row items-center gap-x-3 text-black-900">
            {finishedRefereeLetter && (
              <button
                onClick={(event) => {
                  event.stopPropagation();
                  if (finishedRefereeLetter.url) {
                    window.open(finishedRefereeLetter.url, "_blank");
                  }
                }}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-green-50 transition hover:bg-green-100"
                title={finishedRefereeLetter.fileName || "Open referee letter"}
              >
                <Icon20DocDownloaded />
              </button>
            )}
            {applicationSectionStatusLocal ===
            ApplicationSectionStatusType.ReviewCompleted ? (
              <Badge size="150" type={BadgeType.Success}>
                {t("application:referees.refereeDetails.badge")}
              </Badge>
            ) : (
              <div
                onClick={(event) => {
                  event.stopPropagation();
                  setIsDeleteConfirmModalOpen(true);
                }}
              >
                {/* Delete icon */}
                <Icon24Cancel className="-mt-[1px] text-black-900 transition hover:text-black-600" />
              </div>
            )}

            {/* Toggle referee details icon */}
            {openedReferees.includes(props.field.id) ? (
              <Icon24ChevronDown className="transition hover:text-black-600" />
            ) : (
              <Icon24ChevronUp className="transition hover:text-black-600" />
            )}
          </div>
        </div>

        <Transition
          show={openedReferees.includes(props.field.id)}
          as={Fragment}
          enter="transform ease-out duration-300 transition"
          enterFrom="translate-y-2 opacity-0 sm:-translate-y-2 sm:-translate-x-2"
          enterTo="translate-y-0 opacity-100 sm:translate-x-0"
          leave="transition ease-in duration-200"
          leaveFrom="opacity-200"
          leaveTo="opacity-0"
        >
          <div className="flex flex-col gap-y-5 pt-4 pb-4">
            <div className="mb-3">
              <Heading tag={HeadingTag.H7} className="mt-0 mb-3">
                {t("application:referees.refereeDetails.heading")}
              </Heading>

              <div className="flex flex-wrap gap-1">
                {/* TODO: Add signature likelihood, add tags */}
                {props.field.signatureLikelihoodTagId && (
                  <Badge size="150" type={BadgeType.Success}>
                    {t(
                      `${
                        props.signatureLikelihoodTags.find(
                          (x) =>
                            // GOTCHA: Use type coercion for signatureLikelihoodTagId, cause it's string after select and number as default type from api
                            // TODO: Add parseInt handling for signatureLikelihoodTagId after select change
                            x.id == props.field.signatureLikelihoodTagId
                        )?.i18n
                      }`
                    )}
                  </Badge>
                )}
                {props.field.relationshipTagId && (
                  <Badge
                    size="150"
                    type={BadgeType.Custom}
                    className="bg-blue-50a text-blue-600"
                  >
                    {t(
                      `${
                        props.refereeRelashionshipTags.find(
                          (x) =>
                            // GOTCHA: Use type coercion for relationshipTagId, cause it's string after select and number as default type from api
                            // TODO: Add parseInt handling for relationshipTagId after select change
                            x.id == props.field.relationshipTagId
                        )?.i18n
                      }`
                    )}
                  </Badge>
                )}
                {props.field.features
                  ?.filter((x) => x) // GOTCHA: Features array if not touched comes with undefined value (filled by react-hook-form), so filter it
                  .map((x) => (
                    <Badge
                      key={x.id}
                      size="150"
                      type={BadgeType.Custom}
                      className="border border-gray-150 bg-transparent text-black-600"
                    >
                      {x.name}
                    </Badge>
                  ))}
              </div>
            </div>

            <ControlledTextAreaWithInlineHelp<ApplicantRefereeList>
              control={props.control}
              label={t(
                "application:referees.refereeDetails.applicantRefereeList.label"
              )}
              index={props.index}
              fieldName="personDescription"
              rules={{
                required: true,
              }}
              error={fiedsErrors?.personDescription}
              disabled={isFormDisabled}
              minRows={TextAreaMinRows.MultiLine}
              {...getPersonDescriptionText(props.groupName, t, { isO1Visa })}
              onChange={props.onChange}
            />

            <ControlledTextAreaWithInlineHelp<ApplicantRefereeList>
              control={props.control}
              label={t(
                "application:referees.refereeDetails.applicantRefereeList.label"
              )}
              index={props.index}
              fieldName="assessmentDescription"
              rules={{
                required: true,
              }}
              error={fiedsErrors?.assessmentDescription}
              minRows={TextAreaMinRows.MultiLine}
              disabled={isFormDisabled}
              {...getAssessmentDescriptionText(props.groupName, t)}
              onChange={props.onChange}
            />

            <ControlledTextAreaWithInlineHelp<ApplicantRefereeList>
              control={props.control}
              label={t(
                "application:referees.refereeDetails.applicantRefereeList.label"
              )}
              index={props.index}
              fieldName="meetDescription"
              rules={{
                required: true,
              }}
              error={fiedsErrors?.meetDescription}
              minRows={TextAreaMinRows.MultiLine}
              disabled={isFormDisabled}
              {...getMeetDescriptionText(props.groupName, t)}
              onChange={props.onChange}
            />

            <ControlledTextAreaWithInlineHelp<ApplicantRefereeList>
              control={props.control}
              label={t(
                "application:referees.refereeDetails.applicantRefereeList.label"
              )}
              index={props.index}
              fieldName="commonWorksDescription"
              rules={{
                required: true,
              }}
              error={fiedsErrors?.commonWorksDescription}
              minRows={TextAreaMinRows.MultiLine}
              disabled={isFormDisabled}
              {...getCommonWorksDescriptionText(props.groupName, t)}
              onChange={props.onChange}
            />

            {props.groupName === RefereeGroupName.WorkedWith && (
              <ControlledTextAreaWithInlineHelp<ApplicantRefereeList>
                control={props.control}
                label={t(
                  "application:referees.refereeDetails.applicantRefereeList.label"
                )}
                index={props.index}
                fieldName="possibleWorksDescription"
                rules={{
                  required: true,
                }}
                error={fiedsErrors?.possibleWorksDescription}
                minRows={TextAreaMinRows.MultiLine}
                disabled={isFormDisabled}
                {...getPossibleWorksDescriptionText(t)}
                onChange={props.onChange}
              />
            )}

            <ControlledTextAreaWithInlineHelp<ApplicantRefereeList>
              control={props.control}
              label={t(
                "application:referees.refereeDetails.applicantRefereeList.label"
              )}
              index={props.index}
              fieldName="levelOfExcitement"
              error={fiedsErrors?.levelOfExcitement}
              minRows={TextAreaMinRows.MultiLine}
              disabled={isFormDisabled}
              {...getLevelOfExcitementText(t)}
              onChange={props.onChange}
            />

            {(props.groupName === RefereeGroupName.WorkedWith ||
              props.groupName === RefereeGroupName.DidntWorkWith) && (
              <ControlledTextAreaWithInlineHelp<ApplicantRefereeList>
                control={props.control}
                label={t(
                  "application:referees.refereeDetails.applicantRefereeList.label"
                )}
                index={props.index}
                fieldName="contactDetails"
                rules={{
                  required: true,
                }}
                error={fiedsErrors?.contactDetails}
                minRows={TextAreaMinRows.MultiLine}
                disabled={isFormDisabled}
                {...getContactDetails(t)}
                onChange={props.onChange}
              />
            )}
            <div className="inline-flex">
              {applicationSectionStatusLocal !==
                ApplicationSectionStatusType.ReviewCompleted && (
                <Button
                  type={ButtonType.Primary}
                  onClick={() => {
                    if (props.errors) {
                      setShowErrors(true);
                    } else {
                      setIsSubmitConfirmModalOpen(true);
                    }
                  }}
                >
                  {t(
                    "application:referees.refereeDetails.button.submitForReview"
                  )}
                </Button>
              )}
              {isAdmin(props.ownUser) && (
                <Button
                  className="mx-2"
                  type={ButtonType.Flat}
                  onClick={handleGenerateDoc}
                >
                  {t("application:referees.refereeDetails.button.generateDoc")}
                </Button>
              )}

              {isAdmin(props.ownUser) &&
              applicationSectionStatusLocal &&
              applicationSectionStatusLocal !==
                ApplicationSectionStatusType.Created ? (
                <Button
                  className="inline-flex"
                  type={ButtonType.Flat}
                  onClick={handleSectionRollback}
                >
                  {t(
                    "application:profJourney.new.potential.navigation.button.rollBack"
                  )}
                </Button>
              ) : null}
            </div>
          </div>
        </Transition>
      </div>

      <ConfirmModal
        open={isDeleteConfirmModalOpen}
        heading={
          <Trans
            t={t}
            i18nKey="application:referees.refereeDetails.confirmModel.heading"
            values={{
              name: [
                props.field.firstName ? props.field.firstName : "unknown",
                props.field.lastName ? props.field.lastName : "referee",
              ].join(" "),
            }}
            components={[<span key="i18n-tag" className="break-all" />]}
          />
        }
        text={t("application:referees.refereeDetails.deleteConfirmText")}
        confirmUrl="#"
        confirmButtonText={t(
          "application:referees.refereeDetails.button.confirmButtonText"
        )}
        cancelButtonText={t(
          "application:referees.refereeDetails.button.cancelButtonText"
        )}
        onCollapse={() => {
          setIsDeleteConfirmModalOpen(false);
        }}
        onConfirmClick={(event: MouseEvent) => {
          event.preventDefault();
          setIsDeleteConfirmModalOpen(false);
          props.onDelete();
        }}
      />

      <ConfirmModal
        open={isSubmitConfirmModalOpen}
        gifUrl="/images/gif/referee-details-submit.gif"
        heading={t("application:referees.details.popup.submit.title")}
        text={t("application:referees.details.popup.submit.text")}
        confirmUrl="#"
        confirmButtonText={
          t("application:referees.details.popup.button.yes") || ""
        }
        cancelButtonText={
          t("application:referees.details.popup.button.no") || ""
        }
        onCollapse={() => {
          setIsSubmitConfirmModalOpen(false);
        }}
        onConfirmClick={(event: MouseEvent) => {
          event.preventDefault();
          setIsSubmitConfirmModalOpen(false);
          if (props.isLastRefereeToSubmit) {
            setIsSectionFinishedModalOpen(true);
          } else {
            setIsSubmitSucceedModalOpen(true);
          }
        }}
      />

      <ValidationModal
        open={isSubmitSucceedModalOpen}
        gifUrl="/images/gif/referee-details-submit-succeed.gif"
        gifBgPosition="-14px 0"
        heading={
          <Trans
            t={t}
            i18nKey="application:referees.details.popup.startPreparing.title"
            values={{
              name: [
                props.field.firstName ? props.field.firstName : "unknown",
                props.field.lastName ? props.field.lastName : "referee",
              ].join(" "),
            }}
            components={[<span key="i18n-tag" className="break-all" />]}
          />
        }
        text={t("application:referees.details.popup.startPreparing.text")}
        onCollapse={() => {
          handleConfirmModalYesClick();
          setIsSubmitSucceedModalOpen(false);
        }}
        onGotItClick={() => {
          handleConfirmModalYesClick();
          setIsSubmitSucceedModalOpen(false);
        }}
      />

      {props.isLastRefereeToSubmit && (
        <ValidationModal
          open={isSectionFinishedModalOpen}
          gifUrl="/images/gif/referee-details-finished.gif"
          gifBgPosition="-14px 0"
          heading={t("application:referees.details.popup.successSubmit.title")}
          text={t("application:referees.details.popup.successSubmit.text")}
          onCollapse={() => {
            handleConfirmModalYesClick();
            setIsSectionFinishedModalOpen(false);
          }}
          onGotItClick={() => {
            handleConfirmModalYesClick();
            setIsSectionFinishedModalOpen(false);
          }}
        />
      )}
    </>
  );
};

export default RefereeDetails;
